/**
 * Modal: re-run OCR on a cached screenshot OR type the UTR manually.
 *
 * Extracted from AdminApprovals.jsx during the iteration-49 refactor —
 * the parent page was >1000 LOC. This file owns the single-txn UTR
 * assign dialog. The bulk-walkthrough variant lives in
 * BulkAssignUtrModal.jsx.
 */
import { useState } from "react";
import { Check, Loader2, Sparkles } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import Modal from "@/components/Modal";

/**
 * Modal: re-run OCR on a cached screenshot OR type the UTR manually.
 * Shows the cached screenshot inline so the merchant can verify against
 * the field they're typing into — far better than a blind 'enter UTR'.
 *
 * Calls `<scope>/transactions/{txn_id}/rerun-ocr` (200 always — soft-
 * fails just empty the suggestion) and `/assign-utr` to persist.
 */
export default function AssignUtrModal({ txn, scope, onClose, onSaved }) {
    const [utr, setUtr] = useState("");
    const [rerunBusy, setRerunBusy] = useState(false);
    const [saveBusy, setSaveBusy] = useState(false);
    const [ocrInfo, setOcrInfo] = useState(null);   // {ok, utr, reason}
    const [err, setErr] = useState("");
    const base = scope === "admin" ? "/admin" : "/partner";

    const rerunOcr = async () => {
        setRerunBusy(true); setErr(""); setOcrInfo(null);
        // Hybrid: Tesseract first (free) on the cached screenshot.
        // If it can't read the UTR, fall back to the backend's Gemini
        // call. The backend rerun decodes the data URL on the server,
        // so we just hit that endpoint with no body.
        try {
            if (txn.screenshot && txn.screenshot.startsWith("data:")) {
                const { extractUtrClientSide } = await import("@/lib/clientOcr");
                const resp = await fetch(txn.screenshot);
                const blob = await resp.blob();
                const clientRes = await extractUtrClientSide(blob);
                if (clientRes.utr) {
                    setOcrInfo({ ok: true, utr: clientRes.utr, payment_app: "Other", provenance: "client" });
                    setUtr(clientRes.utr);
                    setRerunBusy(false);
                    return;
                }
            }
        } catch (e) {
            console.warn("client-side OCR fallthrough:", e);
        }
        try {
            const r = await api.post(`${base}/transactions/${txn.txn_id}/rerun-ocr`);
            setOcrInfo(r.data);
            if (r.data?.utr) setUtr(r.data.utr);
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || "OCR rerun failed");
        } finally { setRerunBusy(false); }
    };
    const save = async () => {
        if (utr.trim().length < 6) { setErr("UTR must be at least 6 characters"); return; }
        setSaveBusy(true); setErr("");
        try {
            await api.post(`${base}/transactions/${txn.txn_id}/assign-utr`, { utr: utr.trim() });
            onSaved(utr.trim());
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || "Could not save UTR");
        } finally { setSaveBusy(false); }
    };
    return (
        <Modal testId="assign-utr-modal" onClose={onClose} title={`Assign UTR · ${txn.customer_username || txn.txn_id}`} size="md">
            <div className="space-y-4">
                {txn.screenshot ? (
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-2">
                        <img src={txn.screenshot} alt="screenshot"
                             className="w-full max-h-[40vh] object-contain rounded" />
                    </div>
                ) : (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5">
                        No screenshot stored for this transaction. Type the UTR manually below.
                    </div>
                )}

                {txn.screenshot && (
                    <button
                        data-testid="assign-utr-rerun-ocr"
                        type="button"
                        onClick={rerunOcr}
                        disabled={rerunBusy || saveBusy}
                        className="btn-secondary w-full py-2 rounded-md text-xs flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                        {rerunBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        Re-run smart OCR on this screenshot
                    </button>
                )}

                {ocrInfo && ocrInfo.ok && ocrInfo.utr && (
                    <div data-testid="assign-utr-ocr-success" className="text-[11px] rounded-md p-2 bg-emerald-50 border border-emerald-200 text-emerald-800">
                        <span className="font-medium">Detected:</span> UTR <span className="font-mono">{ocrInfo.utr}</span>
                        {ocrInfo.payment_app && ocrInfo.payment_app !== "Other" && <> · {ocrInfo.payment_app}</>}
                        . Click <span className="font-medium">Save UTR</span> to confirm.
                    </div>
                )}
                {ocrInfo && !ocrInfo.ok && (
                    <div data-testid="assign-utr-ocr-softfail" className="text-[11px] rounded-md p-2 bg-amber-50 border border-amber-200 text-amber-900">
                        <span className="font-medium">OCR couldn&apos;t read this image.</span>{" "}
                        {ocrInfo.reason || "Type the UTR you see in the screenshot."}
                    </div>
                )}

                <div>
                    <label className="label">UTR / Reference number</label>
                    <input
                        data-testid="assign-utr-input"
                        value={utr}
                        onChange={(e) => setUtr(e.target.value)}
                        className="input font-mono"
                        placeholder="12-digit reference"
                        inputMode="numeric"
                        autoFocus
                    />
                </div>

                {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}

                <div className="flex justify-end gap-2 pt-1">
                    <button
                        data-testid="assign-utr-cancel"
                        type="button"
                        onClick={onClose}
                        disabled={saveBusy}
                        className="btn-secondary px-3 py-1.5 rounded-md text-xs"
                    >Cancel</button>
                    <button
                        data-testid="assign-utr-save"
                        type="button"
                        onClick={save}
                        disabled={saveBusy || utr.trim().length < 6}
                        className="btn-emerald px-4 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-60"
                    >
                        {saveBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        Save UTR
                    </button>
                </div>
            </div>
        </Modal>
    );
}
